import { useEffect } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { useAppDispatch, useAppSelector } from './hooks'
import { envSelector } from '../features/environment/environmentSlice'
import { getJoinRoom } from '../api/rest'
import { registerFcmToken, setLastFcmToken } from '../api/fcm'

/**
 * Bridge between the React-Native WebView shell (iris-mobile) and this web app, for incoming-call
 * push notifications. It is a **no-op in a normal browser** — everything is gated on
 * `window.ReactNativeWebView`, which only exists inside the RN WebView.
 *
 * Protocol:
 *  - native → web: the shell calls `window.__irisNativeMessage(JSON.stringify(msg))`.
 *      { type:'fcm-token', token, platform } → register the device token with iris-server
 *      { type:'accept-call', roomId }        → answer the call (reuses the normal accept path)
 *  - web → native: `window.ReactNativeWebView.postMessage(JSON.stringify(msg))`.
 *      { type:'web-ready' } → signals the shell it may (re)send a queued token / pending accepted call.
 *
 * The web app does the token registration because it holds the Keycloak JWT (the native shell,
 * post-WebView-migration, does not).
 */

export interface BridgeDeps {
  registerToken: (token: string, platform?: string) => void
  acceptCall: (roomId: string) => void
}

/** Parse and route a native→web message. Pure + exported for testing. Unknown/garbage is ignored. */
export function handleNativeMessage(raw: string, deps: BridgeDeps): void {
  let msg: any
  try {
    msg = JSON.parse(raw)
  } catch {
    return
  }
  switch (msg?.type) {
    case 'fcm-token':
      if (typeof msg.token === 'string' && msg.token) deps.registerToken(msg.token, msg.platform)
      break
    case 'accept-call':
      if (typeof msg.roomId === 'string' && msg.roomId) deps.acceptCall(msg.roomId)
      break
    default:
      break
  }
}

/** True only inside the React-Native WebView (the shell injects `window.ReactNativeWebView`). */
export function isReactNativeWebView(): boolean {
  return typeof window !== 'undefined' && !!(window as any).ReactNativeWebView
}

/** Post a message to the native shell (no-op outside the WebView). */
export function postToNative(msg: object): void {
  const rn = typeof window !== 'undefined' ? (window as any).ReactNativeWebView : undefined
  if (rn && typeof rn.postMessage === 'function') rn.postMessage(JSON.stringify(msg))
}

/** Installs the native bridge while running inside the RN WebView. Call once from `App`. */
export function useNativeBridge(): void {
  const { keycloak } = useKeycloak()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(envSelector)

  useEffect(() => {
    if (!isReactNativeWebView()) return

    const deps: BridgeDeps = {
      registerToken: (token, platform) => {
        setLastFcmToken(token) // retained so logout can unregister this device
        const jwt = keycloak?.token
        if (jwt) registerFcmToken(jwt, token, platform).catch(err => console.warn('FCM token register failed', err))
      },
      acceptCall: (roomId) => {
        if (user?.uuid) dispatch(getJoinRoom({ userId: user.uuid, roomId }))
      },
    }

    ;(window as any).__irisNativeMessage = (raw: string) => handleNativeMessage(raw, deps)
    // Tell the shell the web app is ready to receive a queued token / pending accepted call.
    postToNative({ type: 'web-ready' })

    return () => {
      if ((window as any).__irisNativeMessage) delete (window as any).__irisNativeMessage
    }
  }, [keycloak, dispatch, user])
}

export default useNativeBridge
